import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import HeroSlideshow from '../components/site/HeroSlideshow';
import FaqAccordion from '../components/site/FaqAccordion';
import GetNoScamButtons from '../components/site/GetNoScamButtons';
import { StackCard, StackCardTitle, TopicCard, TopicGrid } from '../components/site/StackCards';
import { Modal } from '../components/ui';
import { ROUTES } from '../components/site/siteNav';
import MissionVideo from '../components/site/MissionVideo';

/* ------------------------------------------------------------------ *
 * Content
 * ------------------------------------------------------------------ */

const ART_OF_WAR = [
  {
    title: 'Happens to other people',
    to: ROUTES.happensToOtherPeople,
    image: '/images/site/home/card-happens-to-other-people.jpeg',
    imageAlt: 'A woman with grey hair and glasses looking at the camera',
    body: [
      'People believe that scams happen to the other person — they’re confident they won’t be tricked by a phishing email or fall for a confidence scam, catfishing or pig butchering. As a result, they underestimate their own risk of being victimized.',
      'We overlook our personal vulnerabilities when assessing scammer risk, believing we are less likely than others to engage in actions that pose a threat — but that’s not what the NUMBERS say.',
    ],
  },
  {
    title: 'Neurodivergent  Explained',
    to: ROUTES.neurodivergentExplained,
    image: '/images/site/home/card-neurodivergent-explained.jpeg',
    imageAlt: 'An illustrated brain wired into a network of data points',
    body: [
      'In today’s scammer infected internet, the current array of legacy solutions have a proven track record of failure.',
      'Neurodivergent design is a radical departure fostering 3 dimensional interconnected contextual visual-spatial intelligence design. This groundbreaking problem-solving approach produces dynamic solutions.',
    ],
  },
  {
    title: 'Predator Profile',
    to: ROUTES.predatorProfile,
    image: '/images/site/home/card-predator-profile.jpeg',
    imageAlt: 'A figure fishing from a cliff edge above a crowd of people',
    body: [
      'When it comes to cyberthreat actors on the internet, the terms “hacker” and “scammer” are used interchangeably. However, there are unique differences between them, and being able to understand the difference is critical to becoming a digitally empowered user.',
    ],
  },
  {
    title: 'Sun Tzu',
    to: ROUTES.sanTzu,
    image: '/images/site/home/card-san-tzu.jpeg',
    imageAlt: 'A stone statue of a seated warrior general amid clouds',
    body: [
      'Today’s internet is a digital battlefield inhabited by a growing number of global adversaries, enabling them to run complex scams on an unprecedented industrial scale. Sun Tzu, history’s most celebrated and influential military theorist, whose Art of War has transcended its origins to become essential reading, forms the foundational strategy for No / SCAM’s design and approach.',
    ],
  },
];

const BRAINWASHING_CARDS = [
  {
    title: 'Emotional Manipulation',
    to: ROUTES.emotionalManipulation,
    image: '/images/site/home/card-emotional-manipulation.jpeg',
    imageAlt: 'A shattering porcelain face against a dark background',
    body: [
      'Scammers are predators — they’re schooled in the use of dark psychology social engineering techniques. They’re masters at connecting, befriending and coaching their targets to steal their hard-earned money and personal information, robbing their victims of their self-worth and leaving them in emotional tatters.',
      'They leverage these techniques to exploit emotional vulnerabilities, manipulate emotions, and influence decision-making processes to achieve their own self-serving financial goals.',
    ],
  },
  {
    title: 'Abuse Playbook',
    to: ROUTES.abusePlaybook,
    image: '/images/site/home/AdobeStock_611676611.jpeg',
    imageAlt: 'Hands controlling puppet strings',
    body: [
      'From Instagram and Facebook to LinkedIn, scammer profiles are very slick — all designed to draw you in and lower your defenses. Understand the covert, 6 integrated stages of the abuse lifecycle these digital violators use to lure, entice, control and abuse their targets.',
    ],
  },
];

const WRECKAGE_CARDS = [
  {
    title: 'The Senior Challenge',
    to: ROUTES.seniorChallenge,
    image: '/images/site/home/card-senior-challenge.jpeg',
    imageAlt: 'An older couple standing together outside their home',
    body: [
      'Life happens. As we age, our mental and physical abilities diminish over time, making seniors a prime target and more susceptible to the broad array of frauds and schemes. The nexus of wealth, social isolation, loss of mobility and personal independence makes this group an easy mark for these unscrupulous abusers.',
    ],
  },
  {
    title: 'The Family Impact',
    to: ROUTES.familyImpact,
    image: '/images/site/home/card-family-impact.jpeg',
    imageAlt: 'A vintage family portrait',
    body: [
      'Scamming affects the fabric of the entire family; it goes beyond the financial impact, it calls into question trust, judgement and self-reliance. Gone are the retirement investments, monies set aside for medical expenses or long overdue travel and adventure. It’s emotionally exhausting and strains family ties. You are your parents’ emotional and sole financial care giver — all because of a scammer.',
    ],
  },
];

const MONEY_CARDS = [
  {
    title: 'Cash Cow',
    to: ROUTES.cashCow,
    image: '/images/site/home/card-cash-cow.jpeg',
    imageAlt: 'A cartoon of a man milking a cow into money bags',
    body: [
      'These are a list of top revenue producing scams pulled from the annual IC3 Internet Crime Report. These consumer-focused frauds continue to produce blockbuster growth for the array of international criminal organizations. These frauds run the gambit of cybercrime, creating a steady stream of income for these evil doers.',
    ],
  },
  {
    title: 'Bad Dream',
    to: ROUTES.badDream,
    image: '/images/site/home/card-bad-dream.jpeg',
    imageAlt: 'A commemorative bitcoin coin in a presentation box',
    body: [
      'Cryptocurrency is a pseudo-anonymous, loosely regulated market without traditional banking safeguards. There is no central authority to reverse transactions or provide refunds. The decentralized nature, along with the speed of irreversible transaction processing and the ability to transfer value around the world, creates a lot of appeal for scammers to avoid law enforcement and operate across international borders.',
    ],
  },
];


/*
 * The two halves of the threat landscape. Both captions in the slide were the
 * Cash Cow paragraph pasted in as placeholder and cut off mid-sentence, so
 * these describe what each category actually is.
 */

/*
 * Homepage FAQ, from the QA_NuV_FAQs copy supplied 10-06 (spelling and
 * grammar tidied). The doc asked the tracking question twice, as Q8 and Q9,
 * with overlapping answers; they are one entry here. An answer paragraph may
 * be a string or JSX (the Get Started link).
 */
const FAQ_ITEMS = [
  {
    id: 'why',
    question: 'Why do I need to use No / SCAM?',
    answer: [
      'Frauds, schemes and scams come in different variations. At its core, No / SCAM is a cohesive, integrated multi-threat approach. Its purpose-driven design understands the interconnection between social, human and technology, producing a synergistic ScammerAware solution.',
      'It changes the way you interact with the internet. It’s an offensive tool that turns your browser into a context ScammerAware digital concierge. It alerts you to threat patterns, proactively informing you of potential dangers and creating an informed risk decision point in real time.',
      'No / SCAM uses a UniSCAM Human-in-the-Loop architecture, moving past the legacy technical limitations of today’s current crop of solutions into a multidimensional scammer solution threat environment.',
    ],
  },
  {
    id: 'install',
    question: 'How do I install No / SCAM?',
    answer: [
      <>
        Simple step-by-step instructions on how to subscribe, download and install No / SCAM on
        Windows devices can be found on our{' '}
        <Link to={ROUTES.getStarted} className="text-[#2F5F9E] underline">
          Get Started page
        </Link>
        .
      </>,
    ],
  },
  {
    id: 'ai-prompts',
    question: 'What AI prompts do I need to use with No / SCAM?',
    answer: ['None. With No / SCAM you launch, type, surf, learn and explore.'],
  },
  {
    id: 'replace-security',
    question: 'Will No / SCAM replace my antivirus (AV), VPN or identity software?',
    answer: [
      'No. Solutions like Norton, McAfee, Aura or other security solutions zero in on software, security flaws or digital threat signatures. They provide a first line of defense and have a place, but understand these tools are ineffective against scammers. They’re a nuisance, easily outsmarted by these apex predators.',
      'Human manipulation is by far the largest challenge. Advances in AI have made social engineering attacks faster to produce and harder to detect, particularly phishing emails, smishing direct message texts and deepfake voice or video impersonation.',
    ],
  },
  {
    id: 'victim',
    question: 'Will No / SCAM keep me from being a victim of a scam?',
    answer: [
      'It depends. No / SCAM empowers you with information and cutting-edge scammer prevention tools to avoid being a scammer’s next unsuspecting victim. The Human-in-the-Loop Analytical Intelligence (HITL-AI) is specifically designed to meet the continually evolving, complex challenges of today’s internet con artists.',
      'From a technology perspective, HITL-AI adaptive logic leverages human context to understand edge cases. This keeps informed-risk human decision-making at the centerpiece, ensuring that in ambiguous situations humans have the opportunity to review, redirect and override the course of action.',
    ],
  },
  {
    id: 'subscription',
    question: 'Why is No / SCAM a subscription while other services are free?',
    answer: [
      'NuVola AI designed No / SCAM to be an affordable, easy to use, cost effective next-level solution that’s focused on and targets online scammers. Free website validators require user intentionality, have a spotty accuracy record and rely on advertisers for revenue — nothing is free. Our inexpensive subscription model provides flexibility, enabling us to stay true to our mission and giving us the independence to call balls and strikes. It provides us the objectivity and impartiality to make sound risk-based decisions, laser focused on what’s best for our subscribers.',
    ],
  },
  {
    id: 'sci-rating',
    question: 'What is a Scammer Confidence Index (SCI) rating?',
    answer: [
      'It’s a graduated proprietary scammer risk profile scale of objective and subjective factors indicating a website’s level of potential threat exposure. This security matrix ranks known scammer threats, generating and assigning scores based upon defined risk factors.',
    ],
  },
  {
    id: 'tracking',
    question: 'Is my internet activity tracked or used in threat pattern modeling?',
    answer: [
      'No, user data is not tracked or used as any part of No / SCAM’s digital reconnaissance program. Submitted URL requests requiring additional SCI lookups are sent to NuVola AI servers to fulfill your request — no other data is sent. Your request is processed and returned to you, only you.',
    ],
  },
  {
    id: 'multiple-computers',
    question: 'Can I use No / SCAM on more than one computer?',
    answer: ['A No / SCAM subscription is per individual computer.'],
  },
];

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

// The three groups the nav uses, collapsed onto one homepage card. Headings
// carry over from the cards these used to be; the Wreckage pair sits under
// Brainwashing exactly as the "All NuVola AI" directory lists them.
// Each group closes with a Get Started call to action; `cta` is its tagline
// (8-27 mockups, grammar tidied).
const CARD_GROUPS = [
  {
    heading: 'Art of War / Combat in a digital world.',
    cards: ART_OF_WAR,
    cta: 'There has never been a better way to fight scammers.',
  },
  {
    heading: 'Brainwashing / Dark Psychology Social Engineering.',
    cards: [...BRAINWASHING_CARDS, ...WRECKAGE_CARDS],
    cta: 'Designed and built for today\u2019s digital predators while protecting your privacy and dignity.',
  },
  {
    heading: 'Human Wreckage.',
    cards: MONEY_CARDS,
    cta: 'Uniscam design \u2013 next-level ScammerAware thinking.',
  },
];

/**
 * The call to action that closes each card group: wordmark on the left,
 * tagline and the two download buttons on the right, inside a hairline card.
 * 10-1: the buttons replace the single Get Started pill.
 */
const GroupCta = ({ children }) => (
  <div className="mt-6 grid grid-cols-1 gap-5 rounded-[24px] border border-black/20 bg-white px-6 py-6 md:mt-7 md:grid-cols-2 md:items-start md:gap-8 md:rounded-[30px] md:px-8 md:py-7">
    <p className="text-[36px] font-bold leading-none text-black md:text-[52px]">No / SCAM</p>
    <div>
      <p className="text-[18px] leading-snug text-black md:text-[22px]">{children}</p>
      <GetNoScamButtons className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4" />
    </div>
  </div>
);

const HomeV3 = () => {
  usePageTitle('Home');
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="bg-white">
      <HeroSlideshow onPlayVideo={() => setVideoOpen(true)} />

      {/* ---------------- Scroll-stacked cards ---------------- */}
      {/* Light ground so the cards' rounded top corners read as a soft notch
          rather than punching a black hole through the stack. The clip keeps
          the cards' skirts from hanging past the final card — clip-path does
          not create a scroll container, so sticky still works. */}
      {/* pt- gives the first card room to breathe under the hero; without it
          the card's rounded top sits flush against the play bar. */}
      {/* pb- matches, so the card's rounded bottom corners sit on the grey
          ground rather than butting into the white section beneath. */}
      <div className="relative bg-[#EFEFEF] pb-10 pt-10 [clip-path:inset(0)] md:pb-16 md:pt-16">
        {/* 0 — Every sub-page, on one card.
             These used to be five separate stacked cards, each with its own
             banner photography. They are now one card: the groups survive as
             headings, the section imagery is gone, and each sub-page keeps the
             icon it always had. */}
        <StackCard index={0} isLast className="rounded-b-[60px] md:rounded-b-[90px]">
          <StackCardTitle>
            All the ScammerAware tools in one place.
          </StackCardTitle>
          <p className="mx-auto mt-5 max-w-[80ch] text-center text-lg leading-snug text-black md:text-[21px]">
            No / SCAM is designed from the inside out to be the most powerful
            cutting-edge scammer prevention tool, empowering you with the
            information you need to avoid being a scammer&rsquo;s next
            unsuspecting victim.
          </p>
          <img
            src="/images/site/home/scammeraware-in-one-place.jpeg"
            alt="A woman working confidently at her laptop"
            className="mx-auto mt-7 h-[240px] w-full max-w-[900px] rounded-[30px] object-cover shadow-lg small:aspect-[30/11] small:h-auto"
          />

          {CARD_GROUPS.map((group) => (
            <section key={group.heading} className="mt-10 md:mt-14">
              <h3 className="text-center text-[22px] font-bold leading-tight text-black md:text-[30px]">
                {group.heading}
              </h3>
              <TopicGrid>
                {group.cards.map((card) => (
                  <TopicCard key={card.title} {...card}>
                    {card.body.map((paragraph, i) => (
                      <p key={i}>{paragraph}</p>
                    ))}
                  </TopicCard>
                ))}
              </TopicGrid>
              <GroupCta>{group.cta}</GroupCta>
            </section>
          ))}
        </StackCard>

      </div>

      {/* Closing image and copy — a plain full-bleed section, not a stack card.
          It closes the page rather than joining the stack, so it carries no
          card rounding, border or shadow. 8-27: the headline sits above the
          photo instead of over it, and the Get Started button is gone — the
          group CTAs above carry that job now. */}
      <section className="bg-white">
        <h2 className="px-6 py-10 text-center text-[26px] font-bold leading-tight text-black md:py-14 md:text-[44px]">
          Being a ScamAware fighter is just that easy.
        </h2>
        <img
          src="/images/site/home/empowering-you-portrait.jpeg"
          alt="A smiling older man with a white beard"
          className="h-[300px] w-full object-cover object-left small:aspect-[36/13] small:h-auto lg:object-right"
        />
      </section>

      <FaqAccordion items={FAQ_ITEMS} />

      {/* ---------------- Sign-off ---------------- */}
      {/* Replaces the "Turning the hunters" image (8-27 mockup): wordmark,
          rule, and a one-line mission statement on black. */}
      <section className="bg-black px-6 py-16 md:py-24">
        <div className="mx-auto max-w-[900px] text-center">
          <p className="text-[48px] font-light leading-none text-[#BDBDBD] md:text-[96px]">
            No / SCAM
          </p>
          <span aria-hidden="true" className="mx-auto mt-8 block h-px w-full bg-white md:mt-10" />
          <p className="mt-8 text-[24px] font-bold leading-tight text-[#BDBDBD] md:mt-10 md:text-[44px]">
            Empowering you with information to fight online scammers.
          </p>
        </div>
      </section>

      <Modal
        isOpen={videoOpen}
        onClose={() => setVideoOpen(false)}
        size="xlarge"
        aria-label="Video player"
      >
        {/* Only mounted while the modal is open, so closing it stops playback
            and nothing is fetched for visitors who don't open it. Same film
            the Investors page plays. */}
        <MissionVideo />
      </Modal>
    </div>
  );
};

export default HomeV3;
